/**
 * 登入／註冊之後要回到哪裡（`/login?redirect=…`）。
 *
 * `redirect` 來自 URL，也就是**任何人都能寫進去的輸入**：一條
 * `https://stock.example/login?redirect=https://evil.example` 的釣魚連結，
 * 登入完成後把人帶到一個長得一模一樣的假站，再「請重新輸入密碼」。所以只接受
 * 本站的相對路徑，其餘一律退回 `fallback`：
 *  · 必須以 `/` 開頭（這一條本身就擋掉 `javascript:`、`https://…` 這種第一個 `/` 之前
 *    帶著 `:` 的 scheme），但不能是 `//`（協定相對 URL）或 `/\`（瀏覽器把 `\` 當 `/`）；
 *  · 不含控制字元與空白（瀏覽器會先刪掉 tab / 換行，`/\t/evil.example` 就變成 `//evil.example`）；
 *  · 最多 512 字元；
 *  · 不指向登入／註冊／忘記密碼／重設密碼本身（登入完又回到登入頁是一個迴圈）。
 *
 * 最後再用 URL 解析確認它**真的**落在同一個 origin——上面每一條都是便宜的第一道，
 * 解析才是最後的仲裁。
 */
export const DEFAULT_REDIRECT = '/tw/dashboard';

const MAX_REDIRECT_LENGTH = 512;

const AUTH_PAGES: Record<string, true> = {
  '/login': true,
  '/register': true,
  '/forgot-password': true,
  '/reset-password': true,
};

/** 解析用的假 origin：`.invalid` 是保留 TLD，不可能與任何真站重名。 */
const PROBE_ORIGIN = 'https://redirect-probe.invalid';

// eslint 的 no-control-regex 會擋字面控制字元；用 code point 比對。
function hasControlOrSpace(value: string): boolean {
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    if (code <= 0x20 || code === 0x7f) return true;
  }
  return false;
}

export function safeRedirect(raw: unknown, fallback: string): string {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_REDIRECT_LENGTH) {
    return fallback;
  }
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return fallback;
  if (hasControlOrSpace(raw)) return fallback;
  let url: URL;
  try {
    url = new URL(raw, PROBE_ORIGIN);
  } catch {
    return fallback;
  }
  if (url.origin !== PROBE_ORIGIN) return fallback;
  const path = url.pathname.replace(/\/+$/, '') || '/';
  if (Object.hasOwn(AUTH_PAGES, path)) return fallback;
  return `${url.pathname}${url.search}${url.hash}`;
}
